import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { publicApi } from '../api/api';

const CHECKOUT_SESSION_KEY = 'checkout_session_data';
const STATUS_POLL_INTERVAL = 5000;  // start at 5s — reduces 429s on prod
const MAX_RETRIES = 15;
const MAX_BACKOFF = 30000;           // cap at 30s

/**
 * Handles order-status polling with exponential back-off.
 * Returns { startPolling, stopPolling }.
 */
export function useOrderStatusPoller() {
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const hardStopRef = useRef(null);
  const cancelledRef = useRef(false);

  const stopPolling = useCallback(() => {
    cancelledRef.current = true;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (hardStopRef.current) {
      clearTimeout(hardStopRef.current);
      hardStopRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (orderId, { onSuccess, onFailure, onTimeout } = {}) => {
      cancelledRef.current = false;
      let retries = 0;
      let delay = STATUS_POLL_INTERVAL;

      const poll = async () => {
        if (cancelledRef.current) return;
        try {
          const res = await api.get(`/orders/order/${orderId}`);
          if (cancelledRef.current) return; // user cancelled while request was in-flight
          const status = res.data?.data?.status;
          if (status === 'success') {
            stopPolling();
            const formatted = String(orderId).padStart(6, '0');
            sessionStorage.removeItem(CHECKOUT_SESSION_KEY);
            if (onSuccess) onSuccess();
            else navigate(`/order-success/ORD_${formatted}`);
            return;
          }

          if (status === 'failed') {
            stopPolling();
            if (onFailure) onFailure('Order processing failed.');
            return;
          }

          // pending / unknown — progressive backoff, never reset to base
          delay = Math.min(delay * 1.3, MAX_BACKOFF);
          timeoutRef.current = setTimeout(poll, delay);
        } catch (err) {
          if (cancelledRef.current) return;
          retries += 1;
          if (retries >= MAX_RETRIES) {
            stopPolling();
            if (onTimeout) onTimeout();
            return;
          }
          delay = Math.min(delay * 2, MAX_BACKOFF);
          timeoutRef.current = setTimeout(poll, delay);
        }
      };

      stopPolling();
      cancelledRef.current = false; // reset after stopPolling clears it
      poll();

      // 3-minute hard stop — saved to ref so it can be cancelled
      hardStopRef.current = setTimeout(() => {
        if (!cancelledRef.current) {
          stopPolling();
          if (onTimeout) onTimeout();
        }
      }, 180_000);
    },
    [navigate, stopPolling]
  );

  return { startPolling, stopPolling };
}

/**
 * Polls order status using the **public** (no-auth) endpoint.
 * Use this after a Razorpay redirect where the auth token is not available.
 */
export function usePublicOrderStatusPoller() {
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const hardStopRef = useRef(null);
  const cancelledRef = useRef(false);

  const stopPolling = useCallback(() => {
    cancelledRef.current = true;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (hardStopRef.current) {
      clearTimeout(hardStopRef.current);
      hardStopRef.current = null;
    }
  }, []);

  const startPublicPolling = useCallback(
    (orderId, { onSuccess, onFailure, onTimeout } = {}) => {
      cancelledRef.current = false;
      let retries = 0;
      let delay = STATUS_POLL_INTERVAL;

      const poll = async () => {
        if (cancelledRef.current) return;
        try {
          const res = await publicApi.get(`/orders/${orderId}/status`);
          if (cancelledRef.current) return; // user cancelled while request was in-flight
          const status = res.data?.data?.status;

          if (status === 'success') {
            stopPolling();
            if (onSuccess) onSuccess();
            else navigate(`/order-success/ORD_${String(orderId).padStart(6, '0')}`);
            return;
          }

          if (status === 'failed') {
            stopPolling();
            if (onFailure) onFailure('Order processing failed.');
            return;
          }

          // pending — progressive backoff, never reset to base
          delay = Math.min(delay * 1.3, MAX_BACKOFF);
          timeoutRef.current = setTimeout(poll, delay);
        } catch (err) {
          if (cancelledRef.current) return;
          retries += 1;
          if (retries >= MAX_RETRIES) {
            stopPolling();
            if (onTimeout) onTimeout();
            return;
          }
          delay = Math.min(delay * 1.5, MAX_BACKOFF);
          timeoutRef.current = setTimeout(poll, delay);
        }
      };

      stopPolling();
      cancelledRef.current = false; // reset after stopPolling clears it
      poll();

      // 3-minute hard stop — saved to ref so it can be cancelled
      hardStopRef.current = setTimeout(() => {
        if (!cancelledRef.current) {
          stopPolling();
          if (onTimeout) onTimeout();
        }
      }, 180_000);
    },
    [navigate, stopPolling]
  );

  return { startPublicPolling, stopPolling };
}

/**
 * Fallback truth check: asks the backend to verify payment status directly with
 * Razorpay's API. Our own order-status poll (usePublicOrderStatusPoller above) reads
 * orders.status, which is only updated by our webhook — if that poll times out, this
 * is a last resort before showing a failure message, since a payment can already be
 * captured by Razorpay while our own status is still lagging (slow/retrying webhook).
 * Returns true only if Razorpay confirms a captured payment; false on any doubt.
 */
export async function checkRazorpayVerifiedStatus(orderId) {
  try {
    const res = await publicApi.get(`/orders/${orderId}/razorpay-status`);
    return res.data?.data?.status === 'success';
  } catch (_) {
    return false;
  }
}

/**
 * Resolves a Razorpay order id (from the post-payment redirect URL) back to our own
 * astro order id. Used when the checkout session (sessionStorage) is missing — see
 * CheckoutRedirectCallback.jsx — so the frontend can still poll our own order-status
 * endpoint instead of guessing from URL params or trusting a one-off Razorpay check.
 * Returns null if it can't be resolved.
 */
export async function resolveAstroOrderId(razorpayOrderId) {
  try {
    const res = await publicApi.get(`/orders/resolve-by-razorpay-order/${razorpayOrderId}`);
    return res.data?.data?.astroOrderId || null;
  } catch (_) {
    return null;
  }
}

/**
 * Persists checkout session to sessionStorage so the page survives a refresh.
 */
export function saveCheckoutSession(data) {
  try {
    sessionStorage.setItem(
      CHECKOUT_SESSION_KEY,
      JSON.stringify({ ...data, _ts: Date.now() })
    );
  } catch (_) {}
}

/**
 * Reads checkout session data. Returns null if missing or expired (>30 min).
 */
export function loadCheckoutSession() {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_SESSION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - (data._ts || 0) > 30 * 60 * 1000) {
      sessionStorage.removeItem(CHECKOUT_SESSION_KEY);
      return null;
    }
    return data;
  } catch (_) {
    return null;
  }
}

export function clearCheckoutSession() {
  try { sessionStorage.removeItem(CHECKOUT_SESSION_KEY); } catch (_) {}
}

/**
 * Converts a Razorpay-returned upi:// URL to an Android intent:// URL targeting a specific app.
 * If the URL is already intent://, returns it as-is.
 * Falls back to the original URL if the app package is unknown.
 *
 * @param {string} app      - App id: 'gpay' | 'phonepe' | 'paytm' | 'bhim'
 * @param {string} upiUrl   - The upi:// URL returned by Razorpay's API
 * @returns {string}
 */
export function buildAndroidIntentUrl(app, upiUrl) {
  if (!upiUrl) return upiUrl;
  // Already an intent URL — return as-is
  if (upiUrl.startsWith('intent://')) return upiUrl;

  const packages = {
    gpay:    'com.google.android.apps.nbu.paisa.user',
    phonepe: 'com.phonepe.app',
    paytm:   'net.one97.paytm',
    bhim:    'in.org.npci.upiapp',
  };

  const pkg = packages[app];
  // Strip the upi:// scheme; intent:// uses the same host+path
  const withoutScheme = upiUrl.replace(/^upi:\/\//, '');
  const fallback = encodeURIComponent('https://play.google.com/store');

  if (pkg) {
    return `intent://${withoutScheme}#Intent;scheme=upi;package=${pkg};S.browser_fallback_url=${fallback};end`;
  }
  return upiUrl;
}
